import { useQuery } from '@apollo/client'
import { Button, Stack, Typography } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { isAndroid, isIOS } from 'react-device-detect'

import {
  ShortLinkQuery,
  ShortLinkQueryVariables,
} from '@/clients/utoniq-core/schema'
import { QUERY } from '@/components/pages/Ulink/viewModel'
import { config } from '@/config'
import { StorageImage } from '@/core'
import { useTranslation } from '@/core/i18n'

/**
 * Webのないアプリ(DiiCa, はるまき, CLAZYVODKATONIC)の場合、Webページが存在しないことを示すページ
 * ネイティブアプリ(インストールしていない場合はストア)へのリンクを表示する
 */
export const NotFoundWebTemplate: FC = () => {
  const { t } = useTranslation('notFoundWeb')
  const [url, setUrl] = useState('')
  const [finalAppUrl, setFinalAppUrl] = useState('')

  // ページがUlinkの場合、URLを展開する
  const isUlinkPath = url.includes('ulink/')
  const shortLinkId = url.split('ulink/').pop()
  const { data } = useQuery<ShortLinkQuery, ShortLinkQueryVariables>(QUERY, {
    variables: { shortLinkId: shortLinkId ?? '' },
    skip: !isUlinkPath || !shortLinkId,
  })

  const strippedUrl = url.replace(/^https?:\/\//, '')
  const appUrl = isAndroid
    ? `${config.appInfo.android.bundleId}://${strippedUrl}`
    : isIOS
    ? `${config.appInfo.ios.bundleId}://${strippedUrl}`
    : ''

  useEffect(() => {
    // Ulinkの場合redirectUrlをURLスキーマにして保存する
    if (isUlinkPath && data?.shortLink) {
      const redirectUrl = data.shortLink.redirectUrl
      const shortStrippedUrl = redirectUrl.replace(/^https?:\/\//, '')
      const shortAppUrl = isAndroid
        ? `${config.appInfo.android.bundleId}://${shortStrippedUrl}`
        : isIOS
        ? `${config.appInfo.ios.bundleId}://${shortStrippedUrl}`
        : ''
      setFinalAppUrl(shortAppUrl)
    } else {
      setFinalAppUrl(appUrl)
    }
  }, [url, isUlinkPath, appUrl, data])

  const handleClick = () => {
    if (!isAndroid && !isIOS) return

    // URLスキーマでアプリを開き遷移する処理
    const targetUrl = finalAppUrl || appUrl
    if (isIOS) window.location.href = targetUrl
    if (isAndroid) {
      const link = document.createElement('a')
      link.href = targetUrl
      document.body.appendChild(link)
      link.click()
      link.remove()
    }

    // 一定時間後にストアへリダイレクト
    const now = Date.now()
    const timeout = 2000
    setTimeout(() => {
      if (Date.now() - now < timeout + 100) {
        window.location.href = isAndroid
          ? `https://play.google.com/store/apps/details?id=${config.appInfo.android.bundleId}`
          : `https://apps.apple.com/app/id${config.appInfo.ios.storeId}`
      }
    }, timeout)
  }
  useEffect(() => {
    setUrl(window.location.href)
  }, [])
  return (
    <Stack sx={{ maxWidth: 600, width: '100%', margin: '0 auto' }}>
      <Stack sx={{ p: 7, mt: '154px' }} alignItems="center">
        <StorageImage
          src="images/icon.png"
          style={{ objectFit: 'contain', width: 120, height: 120 }}
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ pt: 5 }}
          align="center"
        >
          {t('description')}
        </Typography>
      </Stack>

      <Stack alignItems="center">
        <Button
          component="a"
          size="large"
          onClick={handleClick}
          sx={{ width: 244 }}
        >
          {t('openApp')}
        </Button>
      </Stack>
    </Stack>
  )
}
